import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { BookOpen, Check, Plus, Workflow } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getKnowledgePath, KNOWLEDGE_TAB_PARAMS } from '@/lib/routes'
import { confirmDialog } from '@/lib/utils/window-utils'
import { useFormChangeTracking } from '@/components/ui/form'
import type { Control } from 'react-hook-form'
import type { FormValues } from './agent-detail.schema'
import type {
  KnowledgeBase,
  WorkflowWithActionGroups,
} from '@super/shared-types'

type ResourcesCardProps = {
  control: Control<FormValues>
  knowledgeBases: KnowledgeBase[]
  workflows: WorkflowWithActionGroups[]
  onNavigate: (path: string) => void
}

const UNSAVED_NAVIGATE_MESSAGE =
  'You have unsaved changes. Navigating away will lose your work. Save your agent first, or continue to discard changes?'

export function ResourcesCard({
  control,
  knowledgeBases,
  workflows,
  onNavigate,
}: ResourcesCardProps) {
  const [activeTab, setActiveTab] = useState('knowledge')
  const { hasUnsavedChanges } = useFormChangeTracking()

  const handleNavigate = (path: string) => {
    if (hasUnsavedChanges && !confirmDialog(UNSAVED_NAVIGATE_MESSAGE)) {
      return
    }
    onNavigate(path)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BookOpen className="w-4 h-4" />
          Resources
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2" mobileDropdown={false}>
            <TabsTrigger value="knowledge">Knowledge bases</TabsTrigger>
            <TabsTrigger value="actions">Actions</TabsTrigger>
          </TabsList>

          <TabsContent value="knowledge" className="mt-6">
            <FormField
              control={control}
              name="knowledgeBaseIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Knowledge bases</FormLabel>
                  <FormDescription>
                    Select from your library of knowledge bases
                  </FormDescription>
                  <FormControl>
                    {knowledgeBases.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 border rounded-lg bg-muted/30">
                        <p className="text-sm text-muted-foreground mb-3">
                          No knowledge bases available
                        </p>
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          onClick={() =>
                            handleNavigate(
                              getKnowledgePath({
                                tab: KNOWLEDGE_TAB_PARAMS.KNOWLEDGE_BASES,
                                create: true,
                              }),
                            )
                          }
                        >
                          Create knowledge base
                        </Button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="border rounded-md max-h-[400px] overflow-y-auto">
                            {knowledgeBases.map(kb => {
                              const isSelected = (field.value || []).includes(
                                kb.id,
                              )
                              return (
                                <div
                                  key={kb.id}
                                  className={cn(
                                    'flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors border-b last:border-b-0',
                                    isSelected
                                      ? 'bg-primary/5'
                                      : 'hover:bg-muted/50',
                                  )}
                                  onClick={() => {
                                    const current = field.value || []
                                    if (isSelected) {
                                      field.onChange(
                                        current.filter(
                                          (id: string) => id !== kb.id,
                                        ),
                                      )
                                    } else {
                                      field.onChange([...current, kb.id])
                                    }
                                  }}
                                >
                                  <div
                                    className={cn(
                                      'flex h-4 w-4 items-center justify-center rounded border-2 transition-colors flex-shrink-0',
                                      isSelected
                                        ? 'border-primary bg-primary text-primary-foreground'
                                        : 'border-muted-foreground/30',
                                    )}
                                  >
                                    {isSelected && (
                                      <Check className="h-2.5 w-2.5" />
                                    )}
                                  </div>
                                  <BookOpen className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium">
                                      {kb.title}
                                    </div>
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleNavigate(
                              getKnowledgePath({
                                tab: KNOWLEDGE_TAB_PARAMS.KNOWLEDGE_BASES,
                                create: true,
                              }),
                            )
                          }
                          className="shrink-0"
                        >
                          <Plus className="h-4 w-4" />
                          Create knowledge base
                        </Button>
                      </div>
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>

          <TabsContent value="actions" className="mt-6">
            <FormField
              control={control}
              name="workflowIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Actions</FormLabel>
                  <FormDescription>
                    Select from your library of actions
                  </FormDescription>
                  <FormControl>
                    {workflows.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 border rounded-lg bg-muted/30">
                        <p className="text-sm text-muted-foreground mb-3">
                          No actions available
                        </p>
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          onClick={() => handleNavigate('/actions')}
                        >
                          Create action
                        </Button>
                      </div>
                    ) : (
                      <div className="border rounded-md max-h-[400px] overflow-y-auto">
                        {workflows.map(wf => {
                          const isSelected = field.value?.includes(wf.id)
                          return (
                            <div
                              key={wf.id}
                              className={cn(
                                'flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors border-b last:border-b-0',
                                isSelected
                                  ? 'bg-primary/5'
                                  : 'hover:bg-muted/50',
                              )}
                              onClick={() => {
                                const current = field.value || []
                                if (isSelected) {
                                  field.onChange(
                                    current.filter(
                                      (id: string) => id !== wf.id,
                                    ),
                                  )
                                } else {
                                  field.onChange([...current, wf.id])
                                }
                              }}
                            >
                              <div
                                className={cn(
                                  'flex h-4 w-4 items-center justify-center rounded border-2 transition-colors flex-shrink-0',
                                  isSelected
                                    ? 'border-primary bg-primary text-primary-foreground'
                                    : 'border-muted-foreground/30',
                                )}
                              >
                                {isSelected && (
                                  <Check className="h-2.5 w-2.5" />
                                )}
                              </div>
                              <Workflow className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium">
                                  {wf.name}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}
